import React, { useState } from 'react';
import { Table, Button, message, Popconfirm, Tag, Alert } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CustomAppConfig } from '../../../../../types/customApps';
import { ReplitIntegration } from '../../../../reducers/tools/ReplitReducer';
import CustomAppsDrawer from './CustomAppsDrawer';

interface SettingsCustomAppsPageProps {
  configs: CustomAppConfig[];
  availableApps: ReplitIntegration[];
  fetchingConfigs: boolean;
  fetchingAvailableApps: boolean;
  savingConfig: boolean;
  deletingConfig: boolean;
  createCustomAppConfig: (config: any) => Promise<void>;
  updateCustomAppConfig: (configId: number, config: any) => Promise<void>;
  deleteCustomAppConfig: (configId: number) => Promise<void>;
}

const SettingsCustomAppsPage: React.FC<SettingsCustomAppsPageProps> = ({
  configs,
  availableApps,
  fetchingConfigs,
  fetchingAvailableApps,
  savingConfig,
  deletingConfig,
  createCustomAppConfig,
  updateCustomAppConfig,
  deleteCustomAppConfig,
}) => {
  const { t } = useTranslation();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<CustomAppConfig | null>(null);

  const handleAddConfig = () => {
    setEditingConfig(null);
    setDrawerVisible(true);
  };

  const handleEditConfig = (config: CustomAppConfig) => {
    setEditingConfig(config);
    setDrawerVisible(true);
  };

  const handleDeleteConfig = async (configId: number) => {
    try {
      await deleteCustomAppConfig(configId);
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('validation:unknownApiError'));
    }
  };

  const handleDrawerSubmit = async (values: any) => {
    try {
      if (editingConfig) {
        await updateCustomAppConfig(editingConfig.id, values);
      } else {
        await createCustomAppConfig(values);
      }
      setDrawerVisible(false);
      setEditingConfig(null);
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('validation:unknownApiError'));
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditingConfig(null);
  };

  const getAppName = (appId: number) => {
    const app = availableApps.find(a => a.id === appId);
    return app?.app_id || `App ${appId}`;
  };

  const columns = [
    {
      title: t('customApps:app'),
      dataIndex: 'app_id',
      key: 'app_id',
      render: (appId: number) => getAppName(appId),
    },
    {
      title: t('customApps:displayName'),
      dataIndex: 'display_name',
      key: 'display_name',
      render: (displayName: string, record: CustomAppConfig) =>
        displayName || <span className="text-gray-400">{getAppName(record.app_id)}</span>,
    },
    {
      title: t('customApps:level'),
      dataIndex: 'level',
      key: 'level',
      render: (level: string) => <Tag color="blue">{t(`customApps:levels.${level}`)}</Tag>,
    },
    {
      title: t('customApps:displayOrder'),
      dataIndex: 'display_order',
      key: 'display_order',
      render: (order: number) => order || '-',
    },
    {
      title: t('customApps:enabled'),
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      render: (isEnabled: boolean) =>
        isEnabled ? (
          <Tag icon={<CheckOutlined />} color="success">
            {t('common:yes')}
          </Tag>
        ) : (
          <Tag icon={<CloseOutlined />} color="default">
            {t('common:no')}
          </Tag>
        ),
    },
    {
      title: t('customApps:actions'),
      key: 'actions',
      width: 200,
      render: (_: any, record: CustomAppConfig) => (
        <div className="flex gap-2">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditConfig(record)}
            size="small"
          >
            {t('common:edit')}
          </Button>
          <Popconfirm
            title={t('customApps:messages.confirmDelete')}
            onConfirm={() => handleDeleteConfig(record.id)}
            okText={t('common:yes')}
            cancelText={t('common:no')}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deletingConfig}
              size="small"
            >
              {t('common:delete')}
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  if (fetchingAvailableApps) {
    return <div className="p-4">{t('customApps:loadingApps')}</div>;
  }

  if (availableApps.length === 0) {
    return (
      <div className="p-4">
        <Alert message={t('customApps:noAppsAvailable')} type="info" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold mb-2">{t('customApps:title')}</h2>
          <p className="text-gray-600">{t('customApps:description')}</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddConfig}>
          {t('customApps:addConfiguration')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={configs}
        loading={fetchingConfigs}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: t('customApps:noConfigurations'),
        }}
      />

      <CustomAppsDrawer
        visible={drawerVisible}
        onClose={handleDrawerClose}
        onSubmit={handleDrawerSubmit}
        availableApps={availableApps}
        editingConfig={editingConfig}
        loading={savingConfig}
        existingConfigs={configs}
      />
    </div>
  );
};

export default SettingsCustomAppsPage;
